/**
 * Workflow Executor Service
 *
 * Executes workflows by running nodes in topological order,
 * calling the ModernizeIT API Layer for each node.
 *
 * Pattern: POST to start job → poll status → GET results
 */

import { NODE_EXECUTORS } from './nodeExecutors'

// ── WorkflowRuns tracking helpers ────────────────────────────────────────────
// Fire-and-forget: never let tracking failures break the pipeline.

function _authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

async function _createWorkflowRun(apiBase, runId, accountId, appName, userId) {
  try {
    await fetch(`${apiBase}/workflows/runs`, {
      method: 'POST',
      headers: _authHeaders(),
      body: JSON.stringify({
        workflow_run_id: runId,
        account_id: accountId,
        workflow_id: '',
        workflow_name: appName,
        created_by_user_id: userId || '',
      }),
    })
  } catch (e) {
    console.warn('[WorkflowRuns] create failed (non-fatal):', e.message)
  }
}

async function _recordJob(apiBase, runId, accountId, nodeType, jobId) {
  try {
    await fetch(`${apiBase}/workflows/runs/${runId}/jobs`, {
      method: 'PATCH',
      headers: _authHeaders(),
      body: JSON.stringify({
        account_id: accountId,
        workflow_run_id: runId,
        flow_type: nodeType,
        job_id: jobId,
      }),
    })
  } catch (e) {
    console.warn('[WorkflowRuns] record job failed (non-fatal):', e.message)
  }
}

async function _finalizeRun(apiBase, runId, accountId, status) {
  try {
    await fetch(`${apiBase}/workflows/runs/${runId}/status`, {
      method: 'PATCH',
      headers: _authHeaders(),
      body: JSON.stringify({
        account_id: accountId,
        workflow_run_id: runId,
        status,
      }),
    })
  } catch (e) {
    console.warn('[WorkflowRuns] finalize failed (non-fatal):', e.message)
  }
}

function _generateRunId(accountId, appName) {
  const ts = Math.floor(Date.now() / 1000)
  return `wfrun_${accountId}_${appName}_${ts}`
}

// Map pipeline node types → the specific jobs key they own
const NODE_TYPE_TO_JOB_KEY = {
  'pipeline-upload':              'ingest_job_id',
  'pipeline-code-analysis':       'codeanalysis_job_id',
  'pipeline-dependencies':        'dependencymapper_job_id',
  'pipeline-code-refactor':       'coderefactor_job_id',
  'pipeline-data-analysis':       'dataanalysis_job_id',
  'pipeline-service-generation':  'service_generation_job_id',
  'pipeline-java-packaging':      'java_packaging_job_id',
  'pipeline-monolith-identifier': 'monolithidentifier_job_id',
  'pipeline-discovery':           'discovery_job_id',
  'pipeline-architecture':        'architecture_job_id',
  'pipeline-ai-naming':           'ai_naming_job_id',
  'pipeline-code-translation':    'code_translation_job_id',
}

// Extract the job ID owned by this node type from its result jobs object.
// Executors spread the full context.jobs (which includes upstream IDs) so we
// MUST use the node-type-specific key — otherwise we'd grab the first non-null
// value which is always the ingest_job_id inherited from context.
function _extractJobId(resultJobs, nodeType) {
  if (!resultJobs) return null
  // Prefer the specific key for this node type
  const specificKey = nodeType && NODE_TYPE_TO_JOB_KEY[nodeType]
  if (specificKey && resultJobs[specificKey] != null) return resultJobs[specificKey]
  // Fallback: first non-null value (for simple/legacy executors)
  return Object.values(resultJobs).find(v => v != null) || null
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Workflow execution context that flows through all nodes
 */
export function createExecutionContext(account, application, apiBaseUrl, userId) {
  return {
    // From Account/Application selection
    scout_account_id: account?.account_id || '',
    application_name: application?.app_name || '',

    // User ID for AWS credentials lookup (REQUIRED - 2026-01-14)
    user_id: userId || '',

    // API settings
    api_base_url: apiBaseUrl || 'http://localhost:8000',

    // Accumulated job IDs (populated as nodes complete)
    jobs: {
      ingest_job_id: null,
      codeanalysis_job_id: null,
      coderefactor_job_id: null,
      dependencymapper_job_id: null,
      monolithidentifier_job_id: null,
      dataanalysis_job_id: null,
      discovery_job_id: null,
      architecture_job_id: null,
      service_generation_job_id: null,  // 2026-01-18: Added for workflow tracking
      ai_naming_job_id: null,           // 2026-01-18: Added for workflow tracking
      java_packaging_job_id: null,      // 2026-01-18: Added for workflow tracking
      code_translation_job_id: null,    // 2026-07-30: Code Translation (Path 2 AST + Path 3 SLM)
    },

    // Source hash from ingest (used by downstream nodes)
    source_hash: null,

    // Results from each node
    results: {},
  }
}

/**
 * Event types emitted during workflow execution
 */
export const ExecutionEvents = {
  WORKFLOW_START: 'workflow_start',
  WORKFLOW_COMPLETE: 'workflow_complete',
  WORKFLOW_ERROR: 'workflow_error',
  WORKFLOW_PAUSED: 'workflow_paused',
  WORKFLOW_RESUMED: 'workflow_resumed',
  NODE_START: 'node_start',
  NODE_PROGRESS: 'node_progress',
  NODE_COMPLETE: 'node_complete',
  NODE_ERROR: 'node_error',
  NODE_SKIPPED: 'node_skipped',
  NODE_PAUSED: 'node_paused',
}

/**
 * Workflow Executor class
 * Manages the execution of a workflow by running nodes in order
 */
export class WorkflowExecutor {
  constructor(workflow, context, options = {}) {
    this.workflow = workflow
    this.context = context
    this.options = {
      continueOnError: false,
      ...options,
    }
    this.listeners = new Map()
    this.aborted = false
    this.paused = false
    this.pausedAtNode = null
    this.pauseResolve = null
    this.abortController = new AbortController()
  }

  /**
   * Subscribe to execution events
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
    return () => this.off(event, callback)
  }

  /**
   * Unsubscribe from execution events
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event)
    if (callbacks) {
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  /**
   * Emit an event to all listeners
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event) || []
    callbacks.forEach(cb => cb(data))
  }

  /**
   * Abort the workflow execution
   */
  abort() {
    this.aborted = true
    // If paused, resume first so it can exit cleanly
    if (this.pauseResolve) {
      this.pauseResolve()
      this.pauseResolve = null
    }
    this.abortController.abort()
  }

  /**
   * Pause the workflow execution
   * Workflow will pause before the next node starts
   */
  pause() {
    if (!this.paused && !this.aborted) {
      this.paused = true
      this.emit(ExecutionEvents.WORKFLOW_PAUSED, {
        workflowId: this.workflow.id,
        pausedAtNode: this.pausedAtNode,
      })
    }
  }

  /**
   * Resume the workflow execution
   */
  resume() {
    if (this.paused && !this.aborted) {
      this.paused = false
      this.emit(ExecutionEvents.WORKFLOW_RESUMED, {
        workflowId: this.workflow.id,
      })
      // If waiting at a pause point, continue execution
      if (this.pauseResolve) {
        this.pauseResolve()
        this.pauseResolve = null
      }
    }
  }

  /**
   * Check if workflow is currently paused
   */
  get isPaused() {
    return this.paused
  }

  /**
   * Wait if paused (returns a promise that resolves when resumed)
   */
  async waitIfPaused(nodeId = null) {
    if (this.paused && !this.aborted) {
      this.pausedAtNode = nodeId
      await new Promise(resolve => {
        this.pauseResolve = resolve
      })
    }
  }

  /**
   * Get the abort signal for passing to executors
   */
  get signal() {
    return this.abortController.signal
  }

  /**
   * Get nodes in execution order (topological sort based on edges)
   */
  getOrderedNodes() {
    const nodes = this.workflow.nodes || []
    const edges = this.workflow.edges || []

    // Build adjacency list and in-degree map
    const adjacency = new Map()
    const inDegree = new Map()

    nodes.forEach(node => {
      adjacency.set(node.id, [])
      inDegree.set(node.id, 0)
    })

    edges.forEach(edge => {
      if (adjacency.has(edge.source) && inDegree.has(edge.target)) {
        adjacency.get(edge.source).push(edge.target)
        inDegree.set(edge.target, inDegree.get(edge.target) + 1)
      }
    })

    // Kahn's algorithm for topological sort
    const queue = []
    const result = []

    inDegree.forEach((degree, nodeId) => {
      if (degree === 0) {
        queue.push(nodeId)
      }
    })

    while (queue.length > 0) {
      const nodeId = queue.shift()
      const node = nodes.find(n => n.id === nodeId)
      if (node) {
        result.push(node)
      }

      adjacency.get(nodeId)?.forEach(targetId => {
        inDegree.set(targetId, inDegree.get(targetId) - 1)
        if (inDegree.get(targetId) === 0) {
          queue.push(targetId)
        }
      })
    }

    return result
  }

  /**
   * Execute the workflow
   */
  async execute() {
    if (!this.workflow || !this.workflow.nodes) {
      throw new Error('Invalid workflow: no nodes defined')
    }

    const orderedNodes = this.getOrderedNodes()
    const executionResults = {
      success: true,
      nodesExecuted: 0,
      nodesSkipped: 0,
      nodesFailed: 0,
      results: {},
      errors: [],
    }

    // ── Create WorkflowRun record ──────────────────────────────────────────
    const _runId = _generateRunId(this.context.scout_account_id, this.context.application_name)
    const _apiBase = this.context.api_base_url
    const _accountId = this.context.scout_account_id
    this.context.workflow_run_id = _runId
    await _createWorkflowRun(_apiBase, _runId, _accountId, this.context.application_name, this.context.user_id)
    // ──────────────────────────────────────────────────────────────────────

    this.emit(ExecutionEvents.WORKFLOW_START, {
      workflowId: this.workflow.id,
      totalNodes: orderedNodes.length,
      context: this.context,
    })

    try {
      for (const node of orderedNodes) {
        // Check for abort first
        if (this.aborted) {
          executionResults.success = false
          executionResults.errors.push({ message: 'Workflow aborted by user' })
          break
        }

        // Wait if workflow is paused (before starting next node)
        await this.waitIfPaused(node.id)

        // Check for abort again after potential pause
        if (this.aborted) {
          executionResults.success = false
          executionResults.errors.push({ message: 'Workflow aborted by user' })
          break
        }

        // Check if node is enabled
        if (node.data?.enabled === false) {
          this.emit(ExecutionEvents.NODE_SKIPPED, {
            nodeId: node.id,
            nodeType: node.data?.nodeType,
            reason: 'Node is disabled',
          })
          executionResults.nodesSkipped++
          continue
        }

        // Handle control nodes that pause the workflow
        const nodeType = node.data?.nodeType

        // Control.pause node - pauses workflow when reached
        if (nodeType === 'control.pause') {
          this.emit(ExecutionEvents.NODE_START, {
            nodeId: node.id,
            nodeType,
            config: node.data?.config || {},
          })

          // Emit paused event for UI
          this.emit(ExecutionEvents.NODE_PAUSED, {
            nodeId: node.id,
            nodeType,
            message: node.data?.config?.message || 'Workflow paused at control node',
          })

          // Auto-pause and wait for resume
          this.paused = true
          this.pausedAtNode = node.id
          this.emit(ExecutionEvents.WORKFLOW_PAUSED, {
            workflowId: this.workflow.id,
            pausedAtNode: node.id,
            pausedAtNodeType: nodeType,
            reason: 'control-node',
          })

          await this.waitIfPaused(node.id)

          // Check for abort after pause
          if (this.aborted) {
            executionResults.success = false
            executionResults.errors.push({ message: 'Workflow aborted by user' })
            break
          }

          this.emit(ExecutionEvents.NODE_COMPLETE, {
            nodeId: node.id,
            nodeType,
            result: { paused: true },
          })

          executionResults.nodesExecuted++
          this.context.results[node.id] = { paused: true }
          executionResults.results[node.id] = { paused: true }
          continue
        }

        // Human-approval node - pauses workflow for manual approval
        if (nodeType === 'human-approval') {
          this.emit(ExecutionEvents.NODE_START, {
            nodeId: node.id,
            nodeType,
            config: node.data?.config || {},
          })

          // Emit paused event for UI
          this.emit(ExecutionEvents.NODE_PAUSED, {
            nodeId: node.id,
            nodeType,
            message: node.data?.config?.message || 'Workflow paused for human approval',
          })

          // Auto-pause and wait for resume
          this.paused = true
          this.pausedAtNode = node.id
          this.emit(ExecutionEvents.WORKFLOW_PAUSED, {
            workflowId: this.workflow.id,
            pausedAtNode: node.id,
            pausedAtNodeType: nodeType,
            reason: 'human-approval',
          })

          await this.waitIfPaused(node.id)

          // Check for abort after approval
          if (this.aborted) {
            executionResults.success = false
            executionResults.errors.push({ message: 'Workflow aborted by user' })
            break
          }

          this.emit(ExecutionEvents.NODE_COMPLETE, {
            nodeId: node.id,
            nodeType,
            result: { approved: true },
          })

          executionResults.nodesExecuted++
          this.context.results[node.id] = { approved: true }
          executionResults.results[node.id] = { approved: true }
          continue
        }

        try {
          const nodeResult = await this.executeNode(node)
          executionResults.nodesExecuted++
          executionResults.results[node.id] = this.context.results[node.id]

          // ── Record job ID against this run ─────────────────────────────
          const jobId = _extractJobId(nodeResult?.jobs, node.data?.nodeType)
          if (jobId) {
            _recordJob(_apiBase, _runId, _accountId, node.data?.nodeType || node.id, jobId)
          }
          // ────────────────────────────────────────────────────────────────

          // Check if node has pause_after flag set
          if (node.data?.config?.pause_after === true) {
            console.log(' [PAUSE_AFTER] Node completed, pausing workflow:', node.id, nodeType)

            // Emit paused event for UI
            this.emit(ExecutionEvents.NODE_PAUSED, {
              nodeId: node.id,
              nodeType,
              message: `Workflow paused after ${nodeType} completed`,
            })

            // Auto-pause and wait for resume
            this.paused = true
            this.pausedAtNode = node.id
            this.emit(ExecutionEvents.WORKFLOW_PAUSED, {
              workflowId: this.workflow.id,
              pausedAtNode: node.id,
              pausedAtNodeType: nodeType,
              reason: 'pause-after-flag',
            })

            await this.waitIfPaused(node.id)

            // Check for abort after pause
            if (this.aborted) {
              executionResults.success = false
              executionResults.errors.push({ message: 'Workflow aborted by user' })
              break
            }
          }
        } catch (error) {
          executionResults.nodesFailed++
          executionResults.errors.push({
            nodeId: node.id,
            nodeType: node.data?.nodeType,
            message: error.message,
          })

          this.emit(ExecutionEvents.NODE_ERROR, {
            nodeId: node.id,
            nodeType: node.data?.nodeType,
            error: error.message,
            nodeResult: error.nodeResult || null,
          })

          if (!this.options.continueOnError) {
            executionResults.success = false
            // Throw — outer catch emits WORKFLOW_ERROR and re-throws to useWorkflow.js.
            // This prevents the false "Workflow completed successfully" log.
            throw new Error(`Node '${node.data?.nodeType}' failed: ${error.message}`)
          }
        }
      }

      // ── Finalize run as completed ──────────────────────────────────────
      await _finalizeRun(_apiBase, _runId, _accountId, 'completed')
      // ──────────────────────────────────────────────────────────────────

      // Only emit WORKFLOW_COMPLETE if all nodes succeeded
      this.emit(ExecutionEvents.WORKFLOW_COMPLETE, {
        workflowId: this.workflow.id,
        ...executionResults,
      })

      return executionResults

    } catch (error) {
      executionResults.success = false
      executionResults.errors.push({ message: error.message })

      // ── Finalize run as failed ─────────────────────────────────────────
      await _finalizeRun(_apiBase, _runId, _accountId, 'failed')
      // ──────────────────────────────────────────────────────────────────

      this.emit(ExecutionEvents.WORKFLOW_ERROR, {
        workflowId: this.workflow.id,
        error: error.message,
        ...executionResults,
      })

      throw error
    }
  }

  /**
   * Execute a single node
   */
  async executeNode(node) {
    const nodeType = node.data?.nodeType
    const config = node.data?.config || {}

    // Merge previous execution result into config (for saved flows)
    // This allows executors to reuse data from previous runs (e.g., source_hash)
    const executionResult = node.data?.executionResult
    if (executionResult) {
      console.log('[WorkflowExecutor] Merging executionResult into config:', {
        nodeId: node.id,
        nodeType,
        executionResultKeys: Object.keys(executionResult),
        hasSourceHash: !!executionResult.source_hash,
        hasJobs: !!executionResult.jobs
      })
      Object.assign(config, executionResult)
    } else {
      console.log('[WorkflowExecutor] No executionResult to merge for node:', node.id)
    }

    this.emit(ExecutionEvents.NODE_START, {
      nodeId: node.id,
      nodeType,
      config,
    })

    // Get the executor for this node type
    const executor = NODE_EXECUTORS[nodeType]
    if (!executor) {
      throw new Error(`No executor found for node type: ${nodeType}`)
    }

    // Create progress callback
    const onProgress = (progress, message) => {
      this.emit(ExecutionEvents.NODE_PROGRESS, {
        nodeId: node.id,
        nodeType,
        progress,
        message,
      })
    }

    // Execute the node (pass abort signal for cancellation)
    const result = await executor.execute(config, this.context, onProgress, this.signal)

    // Update context with results
    if (result.jobs) {
      Object.assign(this.context.jobs, result.jobs)
    }
    if (result.source_hash) {
      this.context.source_hash = result.source_hash
    }
    this.context.results[node.id] = result

    this.emit(ExecutionEvents.NODE_COMPLETE, {
      nodeId: node.id,
      nodeType,
      result,
    })

    return result
  }
}

/**
 * Helper to create and run a workflow
 */
export async function runWorkflow(workflow, account, application, apiBaseUrl, options = {}) {
  const context = createExecutionContext(account, application, apiBaseUrl)
  const executor = new WorkflowExecutor(workflow, context, options)
  return executor.execute()
}

export default WorkflowExecutor
